import { useRef, useState, type ChangeEvent } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Download, Upload } from 'lucide-react'
import {
  ApiError,
  downloadActualsTemplateCsv,
  importActualsCsv,
  type ActualsImportResult,
} from '@/api/client'
import { useAppChrome } from '@/context/AppChromeContext'
import { useToast } from '@/components/toast-context'

const SAMPLE = `opportunity_id,2026-1,2026-2,2026-3
00000000-0000-0000-0000-000000000001,12000,11800,11500`

export function ActualsImportPanel() {
  const toast = useToast()
  const { year } = useAppChrome()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [csvText, setCsvText] = useState('')
  const [summary, setSummary] = useState<string | null>(null)
  const [failures, setFailures] = useState<ActualsImportResult['failures']>([])

  const templateMutation = useMutation({
    mutationFn: () => downloadActualsTemplateCsv(year),
    onSuccess: () => {
      toast.success(`Downloaded ${year} actuals template (forecast pre-filled).`)
    },
    onError: (caught) => {
      toast.error(
        caught instanceof ApiError ? caught.message : 'Could not download actuals template.',
      )
    },
  })

  const importMutation = useMutation({
    mutationFn: () => importActualsCsv(csvText),
    onSuccess: (data) => {
      setSummary(
        `Imported ${data.success_count} row(s)` +
          (data.failure_count ? `; ${data.failure_count} failed.` : '.'),
      )
      setFailures(data.failures ?? [])
      if (data.failure_count === 0) {
        toast.success('Actual revenue imported.')
      } else {
        toast.error('Some actuals rows failed — see details below.')
      }
    },
    onError: (caught) => {
      const message = caught instanceof ApiError ? caught.message : 'Actuals import failed.'
      setSummary(message)
      setFailures([])
      toast.error(message)
    },
  })

  function onChooseFile() {
    fileInputRef.current?.click()
  }

  function onFileSelected(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => {
      const text = typeof reader.result === 'string' ? reader.result : ''
      setCsvText(text)
      setSummary(null)
      setFailures([])
      toast.success(`Loaded ${file.name} — review and import when ready.`)
    }
    reader.onerror = () => {
      toast.error('Could not read that file.')
    }
    reader.readAsText(file)
  }

  return (
    <div className="actuals-import">
      <p className="field__hint">
        Download a CSV for <strong>{year}</strong> with customer, opportunity, and forecast month
        columns (<code>YYYY-M</code>). Edit amounts in Excel, save as CSV, then upload or paste
        below. Imported values are stored as <strong>actual</strong> and stay frozen when the
        forecast recalculates. Keep the <code>opportunity_id</code> column unchanged.
      </p>
      <div className="actuals-import__actions">
        <button
          type="button"
          className="button button--secondary"
          disabled={templateMutation.isPending}
          onClick={() => templateMutation.mutate()}
        >
          <Download size={16} aria-hidden />
          {templateMutation.isPending ? 'Preparing…' : `Download ${year} template`}
        </button>
        <button type="button" className="button button--ghost" onClick={onChooseFile}>
          <Upload size={16} aria-hidden />
          Choose CSV file
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          className="visually-hidden"
          onChange={onFileSelected}
        />
      </div>
      <textarea
        className="field__input actuals-import__textarea"
        rows={6}
        spellCheck={false}
        placeholder={SAMPLE}
        value={csvText}
        onChange={(event) => {
          setCsvText(event.target.value)
          setFailures([])
        }}
        aria-label="Actual revenue CSV"
      />
      <div className="actuals-import__actions">
        <button
          type="button"
          className="button button--secondary"
          disabled={!csvText.trim() || importMutation.isPending}
          onClick={() => importMutation.mutate()}
        >
          {importMutation.isPending ? 'Importing…' : 'Import actuals'}
        </button>
      </div>
      {summary ? <p className="field__hint">{summary}</p> : null}
      {failures.length > 0 ? (
        <div className="actuals-import__failures" role="region" aria-label="Import failures">
          <p className="actuals-import__failures-title">Failed rows</p>
          <ul className="actuals-import__failures-list">
            {failures.map((failure) => (
              <li key={`${failure.line}-${failure.reason}`}>
                <strong>Line {failure.line}:</strong> {failure.reason}
                {failure.row?.opportunity_id ? (
                  <span className="actuals-import__failures-id">
                    {' '}
                    ({failure.row.opportunity_id})
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  )
}
